import { FC, useState } from 'react';

import { useElementShown } from '@hh.ru/analytics-js';
import cardCourseElementShown from '@hh.ru/analytics-js-events/build/career_platform/courses/card_course_element_shown';
import { Accordion, Text } from '@hh.ru/magritte-ui';

import CardEdProgramButtonLinkExternal from 'src/components/CardEdProgram/common/CardEdProgramButtonLinkExternal';
import CardEdProgramLogoPromoCompany from 'src/components/CardEdProgram/common/CardEdProgramLogoPromoCompany';
import CardEdProgramPriceDetails from 'src/components/CardEdProgram/common/CardEdProgramPriceDetails';
import CardEdProgramSubscribe from 'src/components/CardEdProgram/common/CardEdProgramSubscribe';
import {
    EdProgramName,
    isEdProgramTypeFollowLink,
    isEdProgramTypeSubscribe,
    EdProgramPurpose,
} from 'src/types/EdProgram';
import { CalculatorSkill } from 'src/types/calculator';
import { GradeId } from 'src/types/grade';

import styles from './styles.less';

interface Props {
    skill: CalculatorSkill;
    professionId: string;
    defaultIsOpen?: boolean;
    gradeId: GradeId;
}

enum ActiveDialog {
    SubscribeToCourse = 'SubscribeToCourse',
}

const SelectedSkillsItem: FC<Props> = ({ skill, professionId, gradeId, defaultIsOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultIsOpen);
    const handleToggle = () => setIsOpen((currentState) => !currentState);

    const [activeDialog, setActiveDialog] = useState<ActiveDialog>();

    const course = skill.course;

    const courseRef = useElementShown(cardCourseElementShown, {
        courseName: course?.name as unknown as EdProgramName,
        streamName: course?.stream?.name,
        providerId: course?.provider?.id || null,
        providerName: course?.provider?.name || null,
        cardType: course?.purpose as unknown as EdProgramPurpose,
        professionId: course?.profession?.id || null,
        priceDetails: JSON.stringify(course?.priceDetails),
    });

    const handleClickSubscribe = () => {
        setActiveDialog(ActiveDialog.SubscribeToCourse);
    };

    const renderContent = () => {
        if (course) {
            return (
                // монтируем реф для аналитики только если коллапс открыт
                <div ref={isOpen ? courseRef : null}>
                    {course.provider && (
                        <div className={styles.itemProvider}>
                            <CardEdProgramLogoPromoCompany name={course.provider.name} iconSrc={course.provider.icon} />
                        </div>
                    )}
                    {course.priceDetails && (
                        <div className={styles.itemPromocode}>
                            <CardEdProgramPriceDetails edProgram={course} />
                        </div>
                    )}
                    {isEdProgramTypeFollowLink(course) && <CardEdProgramButtonLinkExternal edProgram={course} />}
                    {isEdProgramTypeSubscribe(course) && (
                        <CardEdProgramSubscribe.Button onClick={handleClickSubscribe} />
                    )}
                </div>
            );
        }

        return (
            <>
                {skill.description && (
                    <div className={styles.itemDescription}>
                        <Text typography="paragraph-4-regular" style="secondary" Element="p">
                            {skill.description}
                        </Text>
                    </div>
                )}
                <CardEdProgramSubscribe.Button onClick={handleClickSubscribe} />
            </>
        );
    };

    return (
        <Accordion
            padding={16}
            borderRadius={24}
            controlled
            stretched
            expanded={isOpen}
            onClick={handleToggle}
            title={
                <Text
                    typography="subtitle-1-semibold"
                    data-qa={`calculator-selected-skill-${skill.id}-name`}
                    maxLines={2}
                >
                    {skill.name}
                </Text>
            }
        >
            <div data-qa={`calculator-selected-skill-${skill.id}-content`}>{renderContent()}</div>
            <CardEdProgramSubscribe.Dialog
                isVisible={activeDialog === ActiveDialog.SubscribeToCourse}
                onClose={() => setActiveDialog(undefined)}
                professionId={professionId}
                skillId={skill.id}
                gradeId={gradeId}
                dialogSubtitleText={skill.name}
                dialogDescriptionText={skill.description}
                analyticsParams={{
                    gradeId,
                    professionId,
                    courseName: course?.name,
                    skillId: skill.id,
                    skillName: skill.name,
                    providerId: course?.provider?.id,
                    providerName: course?.provider?.name,
                }}
            />
        </Accordion>
    );
};
export default SelectedSkillsItem;
